import { DateTime } from "luxon";
import moment from "moment";
import { NaiveDate } from "./NaiveDate.js";
/**
 * NaiveDateTime은 offset 정보를 제외한 DateTime이다. DbTime 구현한 구현체라고 봐도 무방하다.
 * Immutable을 지향한다.
 */
export class NaiveDateTime {
    static EG_DATE_TIME_FORMAT = "yyyyMMddHHmmss";
    static EG_DATE_TIME_TO_FORMAT = "yyyyMMddHHmm";
    static EG_DATE_TIME_LENGTH = NaiveDateTime.EG_DATE_TIME_FORMAT.length;
    static isNaiveDateTime(testItem) {
        return testItem._isNaiveDateTime;
    }
    /// NaiveDateTime의 Timestamp를 가지는 DateTime instance
    udt;
    _isNaiveDateTime;
    constructor(udt) {
        this.udt = udt;
        this._isNaiveDateTime = true;
    }
    utc(year, month, day, hour, minute, second, millisecond) {
        return new NaiveDateTime(DateTime.utc(year, month, day, hour, minute, second, millisecond));
    }
    static fromFormat(...args) {
        return NaiveDateTime.fromDateTime(DateTime.fromFormat(...args));
    }
    /**
     * Create NaiveDateTime from DateTime of luxon
     *
     * @param dt
     * @example
     * const ldt = DateTime.fromISO("2010-01-01T00:00", { zone: "Asia/Bangkok" }) // 2010-01-01T00:00:00+0700
     * NaiveDateTime.fromDateTime(ldt)                 // 2010-01-01T00:00:00
     */
    static fromDateTime(dt) {
        const udt = dt.setZone("UTC", {
            keepLocalTime: true,
        });
        return new NaiveDateTime(udt);
    }
    /**
     * Create NaiveDateTime from Moment
     *
     * @param dt
     * @example
     * const mdt = moment.tz("2010-01-01T00:00", "Asia/Bangkok") // 2010-01-01T00:00:00+0700
     * NaiveDateTime.fromMoment(mdt)                 // 2010-01-01T00:00:00
     */
    static fromMoment(dt) {
        return new NaiveDateTime(DateTime.fromISO(dt.format(), { setZone: true }).setZone("UTC", { keepLocalTime: true }));
    }
    /**
     * Create NaiveDateTime from milliseconds
     * @param millis UNIX Timestamp by millis
     * @param tz 주어지면 `millis`에 `tz` Timezone을 적용하여 NaiveDateTime을 생성한다. 주어지지 않으면 UTC로 생성한다.
     *
     * @example
     * const millis = 1262278800000 // 2009-12-31T17:00:00Z
     * NaiveDateTime.fromMillis(millis)                 // 2009-12-31T17:00:00
     * NaiveDateTime.fromMillis(millis, "Asia/Bangkok") // 2010-01-01T00:00:00
     * NaiveDateTime.fromMillis(millis, "Asia/Seoul")   // 2010-01-01T02:00:00
     */
    static fromMillis(millis, tz) {
        const ndt = DateTime.fromMillis(millis, { zone: tz ?? "UTC" });
        return new NaiveDateTime(ndt.setZone("UTC", { keepLocalTime: true }));
    }
    static fromSeconds(seconds, tz) {
        return NaiveDateTime.fromMillis(seconds * 1000, tz);
    }
    /**
     * Create NaiveDateTime from JS Date
     *
     * @param dt
     * @param tz 주어지면 `millis`에 `tz` Timezone을 적용하여 NaiveDateTime을 생성한다. 주어지지 않으면 system로 생성한다.
     *
     * @example
     * const dt = new Date("2010-01-01T00:00:00+0700")
     * NaiveDateTime.fromJSDate(dt)                 // 2009-12-31T17:00:00
     * NaiveDateTime.fromJSDate(dt, "Asia/Bangkok") // 2010-01-01T00:00:00
     * NaiveDateTime.fromJSDate(dt, "Asia/Seoul")   // 2010-01-01T02:00:00
     */
    static fromJSDate(dt, tz) {
        const ndt = DateTime.fromMillis(dt.valueOf(), { zone: tz ?? "system" });
        const udt = ndt.setZone("UTC", { keepLocalTime: true });
        return new NaiveDateTime(udt);
    }
    /**
     * Create NaiveDateTime from ISO8601 string.
     *
     * @param input ISO8601 string, it can be used with or without offset.
     * @param tz 주어지면 `input`에 `tz` Timezone을 적용하여 NaiveDateTime을 생성한다. 주어지지 않으면 UTC로 생성한다.
     *
     * @example
     * const str = "2010-01-01T00:00+0700"
     * new NaiveDateTime(str)                 // 2010-01-01T00:00:00Z
     * new NaiveDateTime(str, "Asia/Bangkok") // 2010-01-01T00:00:00Z
     * new NaiveDateTime(str, "Asia/Seoul")   // 2009-01-01T02:00:00Z
     *
     * // `string` 타입에 offset 정보가 없는 경우 `tz`와 관계 없이 그 자체가 dbtime이 된다.
     * const str = "2010-01-01T00:00:00"
     * NaiveDateTime.fromISO(str)                 // 2010-01-01T00:00:00Z
     * NaiveDateTime.fromISO(str, "Asia/Bangkok") // 2010-01-01T00:00:00Z
     * NaiveDateTime.fromISO(str, "Asia/Seoul")   // 2009-01-01T00:00:00Z
     */
    static fromISO(input, tz) {
        const isoDt = DateTime.fromISO(input, { zone: tz ?? "UTC", setZone: tz ? false : true });
        return new NaiveDateTime(isoDt.setZone("UTC", { keepLocalTime: true }));
    }
    static fromNaiveDate(ndt) {
        return NaiveDateTime.fromMillis(ndt.valueOf());
    }
    /// parse yyyyMMddHHmmss
    static fromEgDtString(input) {
        return NaiveDateTime.fromFormat(input.padEnd(NaiveDateTime.EG_DATE_TIME_LENGTH, "0"), NaiveDateTime.EG_DATE_TIME_FORMAT);
    }
    /**
     * `tz`에 해당하는 현재 시각을 반환한다.
     */
    static now(tz) {
        return NaiveDateTime.fromMillis(Date.now(), tz);
    }
    // Convert NaiveDateTime to DateTime with zone
    get year() {
        return this.udt.year;
    }
    get month() {
        return this.udt.month;
    }
    get day() {
        return this.udt.day;
    }
    get hour() {
        return this.udt.hour;
    }
    get minute() {
        return this.udt.minute;
    }
    get second() {
        return this.udt.second;
    }
    get millisecond() {
        return this.udt.millisecond;
    }
    get weekday() {
        return this.udt.weekday;
    }
    get isValid() {
        return this.udt.isValid;
    }
    /**
     * Add timezone offset to NaiveDateTime.
     *
     * @example
     * const dt = NaiveDateTime.fromISO("2010-01-01T00:00:00")
     *
     * // Add Asia/Seoul(+09:00) timezone offset
     * dt.plusTzOffset("Asia/Seoul")       // 2010-01-01T09:00:00
     */
    plusTzOffset(tz) {
        const dt = this.toDateTime("UTC").setZone(tz);
        return new NaiveDateTime(dt.setZone("UTC", { keepLocalTime: true }));
    }
    /**
     * Subtract timezone offset from NaiveDateTime.
     *
     * @example
     * const dt = NaiveDateTime.fromISO("2010-01-01T00:00:00")
     *
     * // Subtract Asia/Seoul(+09:00) timezone offset
     * dt.minusTzOffset("Asia/Seoul")       // 2009-12-31T15:00:00
     */
    minusTzOffset(tz) {
        const dt = this.toDateTime(tz);
        return new NaiveDateTime(dt.setZone("UTC"));
    }
    plus(...args) {
        return new NaiveDateTime(this.udt.plus(...args));
    }
    minus(...args) {
        return new NaiveDateTime(this.udt.minus(...args));
    }
    diff(dt) {
        return this.udt.diff(dt.udt);
    }
    set(...args) {
        return new NaiveDateTime(this.udt.set(...args));
    }
    startOf(...args) {
        return new NaiveDateTime(this.udt.startOf(...args));
    }
    endOf(...args) {
        return new NaiveDateTime(this.udt.endOf(...args));
    }
    valueOf() {
        return this.udt.valueOf();
    }
    toDateTime(tz) {
        return this.udt.setZone(tz, { keepLocalTime: true });
    }
    toFormat(...args) {
        return this.udt.toFormat(...args);
    }
    toISO(opts) {
        opts = opts ?? {};
        if (opts.includeOffset == null) {
            opts.includeOffset = false;
        }
        return this.udt.toISO(opts);
    }
    toMoment(tz) {
        return moment.tz(this.toISO(), tz);
    }
    toJSDate(tz) {
        return this.toDateTime(tz ?? "system").toJSDate();
    }
    toNaiveDate() {
        return new NaiveDate(this.year, this.month, this.day);
    }
    toEgDtString() {
        return this.udt.toFormat(NaiveDateTime.EG_DATE_TIME_TO_FORMAT);
    }
    equals(other) {
        return this.udt.equals(other.udt);
    }
    toMillis() {
        return this.udt.toMillis();
    }
    toSeconds() {
        return this.udt.toSeconds();
    }
}
